import React, { useState } from 'react';
import { Modal, Table, Button, Input, Form, Tag, message } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { expenseCategory, initExpenseCategory } from '../../model/expense';
import { api_expense_category } from '../../api/expense';
import { useTablePagination } from '../common/table-pagination';
import { deleteFailureMessage } from '../../utils/write-result';

interface Props {
    open: boolean;
    categories: expenseCategory[];
    onClose: () => void;
    onRefresh: () => void;
}

const COLOR_PRESETS = [
    { label: 'ส้ม', value: '#FF7A59' },
    { label: 'เหลืองทอง', value: '#F59E0B' },
    { label: 'เขียวมรกต', value: '#10B981' },
    { label: 'ฟ้าสดใส', value: '#3B82F6' },
    { label: 'ม่วงเข้ม', value: '#8B5CF6' },
    { label: 'ชมพู', value: '#EC4899' },
    { label: 'เทาเข้ม', value: '#64748B' },
];

const ExpenseCategoryModal: React.FC<Props> = ({ open, categories, onClose, onRefresh }) => {
    const [editingCategory, setEditingCategory] = useState<expenseCategory | null>(null);
    const [isAddMode, setIsAddMode] = useState(false);
    const [selectedColor, setSelectedColor] = useState<string>('#FF7A59');
    const [form] = Form.useForm();
    const pagination = useTablePagination();

    const handleStartAdd = () => {
        setIsAddMode(true);
        setEditingCategory(null);
        setSelectedColor('#FF7A59');
        form.resetFields();
        form.setFieldsValue({
            code: '',
            name: '',
            color: '#FF7A59',
            remark: '',
            status: 1
        });
    };

    const handleStartEdit = (record: expenseCategory) => {
        setIsAddMode(false);
        setEditingCategory(record);
        const col = record.color || '#FF7A59';
        setSelectedColor(col);
        form.setFieldsValue({
            code: record.code,
            name: record.name,
            color: col,
            remark: record.remark || '',
            status: record.status
        });
    };

    const handleColorChange = (newColor: string) => {
        setSelectedColor(newColor);
        form.setFieldsValue({ color: newColor });
    };

    const handleCancelForm = () => {
        setIsAddMode(false);
        setEditingCategory(null);
        form.resetFields();
    };

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            const payload: any = {
                ...initExpenseCategory,
                ...(editingCategory || {}),
                ...values,
                color: selectedColor || values.color || '#FF7A59',
                name: (values.name || '').trim(),
            };

            if (editingCategory && editingCategory.id > 0) {
                payload.code = (values.code || form.getFieldValue('code') || editingCategory.code || '').toUpperCase().trim();
                const res = await api_expense_category('UPDATE', payload, editingCategory.id);
                if (res) {
                    message.success('แก้ไขหมวดหมู่ค่าใช้จ่ายสำเร็จ');
                    handleCancelForm();
                    onRefresh();
                } else {
                    message.error('แก้ไขหมวดหมู่ไม่สำเร็จ กรุณาลองใหม่อีกครั้ง');
                }
            } else {
                delete payload.code;
                const res = await api_expense_category('INSERT', payload);
                if (res) {
                    message.success('เพิ่มหมวดหมู่ค่าใช้จ่ายสำเร็จ');
                    handleCancelForm();
                    onRefresh();
                } else {
                    message.error('เพิ่มหมวดหมู่ไม่สำเร็จ กรุณาตรวจสอบข้อมูลอีกครั้ง');
                }
            }
        } catch (e: any) {
            if (e?.errorFields) {
                return;
            }
            const errorMsg = e?.response?.data?.message || e?.message || 'เกิดข้อผิดพลาดในการบันทึกหมวดหมู่ กรุณาลองใหม่อีกครั้ง';
            message.error(errorMsg);
        }
    };

    const handleDelete = (id: number) => {
        Modal.confirm({
            title: 'ยืนยันการลบหมวดหมู่',
            content: 'คุณต้องการลบหมวดหมู่ค่าใช้จ่ายนี้หรือไม่? รายการค่าใช้จ่ายที่เคยใช้หมวดหมู่นี้จะยังคงอยู่',
            okText: 'ลบ',
            cancelText: 'ยกเลิก',
            okButtonProps: { danger: true },
            centered: true,
            async onOk() {
                try {
                    await api_expense_category('DELETE', {}, id);
                    message.success('ลบหมวดหมู่เรียบร้อยแล้ว');
                    onRefresh();
                } catch (e) {
                    message.error(deleteFailureMessage(e, 'ไม่สามารถลบหมวดหมู่ได้'));
                }
            }
        });
    };

    const columns: ColumnsType<expenseCategory> = [
        {
            title: 'ลำดับ',
            dataIndex: 'key',
            key: 'key',
            width: '60px',
            align: 'center',
            render: (_t, _r, idx) => idx + 1
        },
        {
            title: 'รหัส',
            dataIndex: 'code',
            key: 'code',
            width: '100px',
            render: (code) => <Tag color="default" style={{ fontWeight: 700 }}>{code}</Tag>
        },
        {
            title: 'ชื่อหมวดหมู่ค่าใช้จ่าย',
            dataIndex: 'name',
            key: 'name',
            render: (name, record) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{
                        width: '14px',
                        height: '14px',
                        borderRadius: '50%',
                        background: record.color || '#FF7A59',
                        display: 'inline-block',
                        boxShadow: `0 0 0 2px rgba(0,0,0,0.1)`
                    }} />
                    <span style={{ fontWeight: 600 }}>{name}</span>
                </div>
            )
        },
        {
            title: 'สีประจำหมวด',
            dataIndex: 'color',
            key: 'color',
            width: '110px',
            render: (color) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{
                        width: '18px',
                        height: '18px',
                        borderRadius: '4px',
                        background: color || '#FF7A59',
                        display: 'inline-block'
                    }} />
                    <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary, #64748B)' }}>
                        {color || '#FF7A59'}
                    </span>
                </div>
            )
        },
        {
            title: 'คำอธิบาย',
            dataIndex: 'remark',
            key: 'remark',
            render: (remark) => <span style={{ color: '#64748B', fontSize: '13px' }}>{remark || '-'}</span>
        },
        {
            title: 'การดำเนินการ',
            key: 'action',
            width: '110px',
            align: 'center',
            render: (_, record) => (
                <div className="table-action-group" style={{ justifyContent: 'center' }}>
                    <Button
                        type="text"
                        size="small"
                        className="table-action-button edit"
                        icon={<FontAwesomeIcon icon="pen-to-square" style={{ color: '#3B82F6' }} />}
                        onClick={() => handleStartEdit(record)}
                        title="แก้ไข"
                        data-testid={`btn-edit-cat-${record.id}`}
                    />
                    <Button
                        type="text"
                        size="small"
                        className="table-action-button delete"
                        icon={<FontAwesomeIcon icon="trash-can" style={{ color: '#EF4444' }} />}
                        onClick={() => handleDelete(record.id)}
                        title="ลบ"
                        data-testid={`btn-del-cat-${record.id}`}
                    />
                </div>
            )
        }
    ];

    return (
        <Modal
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '18px', fontWeight: 700 }}>
                    <span style={{ fontSize: '20px' }}>🏷️</span>
                    <span>จัดการหมวดหมู่ค่าใช้จ่าย (Expense Categories)</span>
                </div>
            }
            open={open}
            onCancel={onClose}
            width={780}
            footer={null}
            centered
            data-testid="expense-category-modal"
        >
            <div style={{ marginTop: '14px' }}>
                {!isAddMode && !editingCategory ? (
                    <div style={{ marginBottom: '14px', display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            type="primary"
                            icon={<FontAwesomeIcon icon="plus" style={{ marginRight: '6px' }} />}
                            onClick={handleStartAdd}
                            style={{
                                background: 'linear-gradient(135deg, #FFA07A 0%, #FF6B4A 100%)',
                                border: 'none',
                                borderRadius: '8px',
                                fontWeight: 700
                            }}
                            data-testid="btn-add-category"
                        >
                            เพิ่มหมวดหมู่ใหม่
                        </Button>
                    </div>
                ) : (
                    <div className="expense-category-form-box">
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                            <div style={{ fontWeight: 700, fontSize: '15px', color: '#FF7A59' }}>
                                {isAddMode ? '➕ เพิ่มหมวดหมู่ค่าใช้จ่ายใหม่' : '✏️ แก้ไขหมวดหมู่ค่าใช้จ่าย'}
                            </div>
                            {!isAddMode && editingCategory && (
                                <div style={{ fontSize: '13px', color: 'var(--text-secondary, #64748B)' }} data-testid="display-category-code">
                                    รหัสหมวดหมู่: <Tag color="orange" style={{ fontWeight: 700 }}>{editingCategory.code}</Tag>
                                </div>
                            )}
                        </div>
                        <Form form={form} layout="vertical">
                            <Form.Item
                                name="name"
                                label="ชื่อหมวดหมู่ค่าใช้จ่าย"
                                rules={[{ required: true, message: 'กรุณาระบุชื่อหมวดหมู่' }]}
                            >
                                <Input placeholder="เช่น ค่าสาธารณูปโภค, ค่าเช่าร้าน" />
                            </Form.Item>

                            <Form.Item label="สีประจำหมวดหมู่">
                                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                                    {COLOR_PRESETS.map(c => {
                                        const isSelected = selectedColor.toLowerCase() === c.value.toLowerCase();
                                        return (
                                            <button
                                                key={c.value}
                                                type="button"
                                                onClick={() => handleColorChange(c.value)}
                                                className={`category-color-btn ${isSelected ? 'active' : ''}`}
                                                style={{ background: c.value }}
                                                data-testid={`btn-color-${c.value}`}
                                            >
                                                {isSelected && <span>✓</span>}
                                                <span>{c.label}</span>
                                            </button>
                                        );
                                    })}

                                    {/* Custom Color Picker */}
                                    <div className="custom-color-picker-box" title="เลือกสีอื่นๆ แบบกำหนดเอง">
                                        <input
                                            type="color"
                                            className="custom-color-input"
                                            value={selectedColor}
                                            onChange={(e) => handleColorChange(e.target.value)}
                                            data-testid="input-custom-color"
                                        />
                                        <span style={{ fontSize: '12px', fontWeight: 700, color: selectedColor }}>
                                            {selectedColor}
                                        </span>
                                    </div>
                                </div>
                            </Form.Item>

                            <Form.Item name="remark" label="คำอธิบาย / หมายเหตุ">
                                <Input.TextArea rows={2} placeholder="เช่น ค่าน้ำประปา, ค่าไฟฟ้า, ค่าเน็ต" />
                            </Form.Item>

                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                                <Button onClick={handleCancelForm}>ยกเลิก</Button>
                                <Button
                                    type="primary"
                                    onClick={handleSave}
                                    style={{
                                        background: 'linear-gradient(135deg, #FFA07A 0%, #FF6B4A 100%)',
                                        border: 'none',
                                        borderRadius: '8px',
                                        fontWeight: 700
                                    }}
                                    data-testid="btn-save-category"
                                >
                                    บันทึกหมวดหมู่
                                </Button>
                            </div>
                        </Form>
                    </div>
                )}

                <Table
                    columns={columns}
                    dataSource={categories}
                    rowKey="id"
                    scroll={{ y: 280 }}
                    pagination={pagination}
                    size="small"
                />
            </div>
        </Modal>
    );
};

export default ExpenseCategoryModal;
